import { isDefined } from "@homebound/activesupport";
import {
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  simpleHeader,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { CostType } from "src/generated/graphqlTypes";
import { TruncatedNameListCell } from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/TruncatedNameListCell";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { openNewTab } from "src/utils/window";
import { PlanBasedBidsTableHeader } from "./PlanBasedBidsTableHeader";
import { EditFormValues } from "./PlanBasedBidsTableV2";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment,
  PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment,
} from "./PlanBasedBidsTableV2.generated";
import {
  tradeColumnDescriptors,
  tradeColumnHeader,
  tradeCostCell,
  TradeCostColumnsData,
} from "./planBasedTradeColumns";

type PlanBasedScopeTableProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  // The bid plan group this table renders (its header, dimensions, and the lines we look up costs from).
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  // This BPG's scope buckets: matched line buckets (groupLine != null) plus the one catch-all (groupLine == null).
  scopedLines: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[];
  mode: "view" | "edit";
  formState: ObjectState<EditFormValues> | undefined;
  bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined;
  searchFilter?: string;
};

// The Show-Scope view for a single bid plan group: the BPG header, then one parent row per matched bid line
// (labeled by its child group, carrying the bidder cost columns) over its takeoff line items, and a final
// standalone "Scope also covered by {group}" row for the catch-all bucket
export function PlanBasedScopeTable(props: PlanBasedScopeTableProps) {
  const { bidPackage, bidPlanGroup, scopedLines, mode, formState, bidContractRevision, searchFilter } = props;
  const activeBidders = bidPackage.activeBidders;

  // When narrowed to a specific BCR, only render that BCR's bidder; otherwise render all active bidders.
  const bidders = useMemo(
    () =>
      bidContractRevision
        ? activeBidders.filter((b) => b.bidContract?.id === bidContractRevision.bidContract.id)
        : activeBidders,
    [activeBidders, bidContractRevision],
  );
  const bcrs = useMemo(
    () => (bidContractRevision ? [bidContractRevision] : bidPackage.latestVersion.latestBidContractRevisions),
    [bidContractRevision, bidPackage],
  );

  const costRowLookup = useCallback(
    (bpglId: string, costCodeId: string) =>
      formState?.costs.rows.find((r) => r.value.groupLineId === bpglId && r.value.costCodeId === costCodeId),
    [formState],
  );

  const tradeCostContext: TradeCostColumnsData = useMemo(
    () => ({
      activeBidders: bidders,
      bidContractRevisions: bcrs,
      costCodes: bidPackage.costCodes,
      mode,
      costRowLookup,
    }),
    [bidders, bcrs, bidPackage.costCodes, mode, costRowLookup],
  );

  const columns = useMemo(() => createColumns(tradeCostContext, bidders.nonEmpty), [tradeCostContext, bidders]);
  const rows = useMemo(() => createRows(bidPlanGroup, scopedLines), [bidPlanGroup, scopedLines]);

  return (
    <div>
      <PlanBasedBidsTableHeader bidPlanGroup={bidPlanGroup} displayName={bidPlanGroup.displayName} />
      <GridTable columns={columns} filter={searchFilter} rows={rows} id={`scope-${bidPlanGroup.id}`} />
    </div>
  );
}

// A matched bid line, or the catch-all bucket, rendered as a parent row over its takeoff line items. Matched
// rows carry `line` so the bidder cost columns render; the catch-all has no line (and no cost).
type GroupRow = { kind: "group"; data: { name: string; line?: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment } };
// A single takeoff line item, rendered with the wide scope columns.
type TliRow = { kind: "tli"; data: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment };
type HeaderRow = { kind: "header"; data: undefined };
type Row = HeaderRow | GroupRow | TliRow;

function createColumns(ctx: TradeCostColumnsData, hasTrades: boolean): GridColumn<Row>[] {
  const scopeColumns: GridColumn<Row>[] = [
    collapseColumn<Row>({ tli: emptyCell }),
    ...maybeAddIdColumn(column<Row>({ header: "ID", group: emptyCell, tli: (tli) => tli.id, mw: "100px" })),
    column<Row>({
      header: "Item Code",
      // The parent (bid line / catch-all) labels the first column
      group: ({ name }) => ({ content: name, css: Css.smSb.gray900.truncate.$ }),
      tli: (tli) => ({ content: `${tli.item.fullCode} ${tli.item.name}`, onClick: () => openTakeoffLineInNewTab(tli) }),
      mw: "300px",
    }),
    column<Row>({ header: "Name", group: emptyCell, tli: (tli) => tli.name }),
    column<Row>({ header: "Cost Type", group: emptyCell, tli: (tli) => tli.costTypeDetail.name }),
    column<Row>({ header: "Location", group: emptyCell, tli: (tli) => tli.location.displayLocationPath }),
    column<Row>({
      header: "Option",
      group: emptyCell,
      // Show the takeoff line's option names; the cell truncates and a tooltip reveals the full list on hover.
      tli: (tli) => {
        const names = tli.options.sortByKey("order").map((rpo) => rpo.name);
        return { content: <TruncatedNameListCell nameList={names} /> };
      },
      mw: "200px",
    }),
    column<Row>({
      header: "Material/Labor",
      group: emptyCell,
      // Labor lines have no material variant, so just label them "Labor"; everything else shows its material code.
      tli: (tli) => (tli.costTypeDetail.code === CostType.Labor ? "Labor" : tli.materialVariant?.code),
    }),
    numericColumn<Row>({
      header: "QTY",
      group: emptyCell,
      // "N/A" for units that ignore quantity (e.g. Lump Sum/Allowance), matching the takeoff scope table.
      tli: (tli) => (tli.unitOfMeasure.useQuantity ? (tli.quantity ?? "") : "N/A"),
    }),
    column<Row>({ header: "UoM", group: emptyCell, tli: (tli) => tli.unitOfMeasure.abbreviation }),
  ];

  // The shared bidder × cost-code cost columns, rendered on the matched bid line rows so costs can still be
  // entered/viewed while Show Scope is on. Skip them entirely when there are no bidders yet.
  const tradeColumns = hasTrades
    ? tradeColumnDescriptors(ctx).map((d) =>
        numericColumn<Row>({
          header: tradeColumnHeader(d),
          group: ({ line }) => (line ? tradeCostCell(line, d, ctx) : emptyCell),
          tli: emptyCell,
          w: "300px",
        }),
      )
    : [];

  return [...scopeColumns, ...tradeColumns];
}

function createRows(
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment,
  scopedLines: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[],
): GridDataRow<Row>[] {
  // Each matched bid line is a parent row (labeled by its options, carrying the bidder cost) over its takeoff
  // line items. The BE already orders these contiguously per child group.
  const lineRows = scopedLines
    .filter((sl) => isDefined(sl.groupLine))
    .map((sl) => ({
      kind: "group" as const,
      id: sl.groupLine!.id,
      data: { name: sl.groupLine!.displayName, line: sl.groupLine! },
      children: sl.takeoffLineItems.map((tli) => ({
        kind: "tli" as const,
        id: `${sl.groupLine!.id}:${tli.id}`,
        data: tli,
      })),
    }));

  // The single catch-all bucket (groupLine == null) renders as a standalone parent row at the end.
  const unmatchedOwnedScope = scopedLines.find((sl) => !isDefined(sl.groupLine));
  const catchAllRow =
    unmatchedOwnedScope && unmatchedOwnedScope.takeoffLineItems.nonEmpty
      ? [
          {
            kind: "group" as const,
            id: unmatchedOwnedScope.id, // owned:bidPlanGroupId
            data: { name: `Scope also covered by ${bidPlanGroup.displayName}` },
            children: unmatchedOwnedScope.takeoffLineItems.map((tli) => ({
              kind: "tli" as const,
              id: `${unmatchedOwnedScope.id}:${tli.id}`,
              data: tli,
            })),
          },
        ]
      : [];

  return [simpleHeader, ...lineRows, ...catchAllRow];
}

// Open the takeoff line item in a new tab, pre-filtered to its cost code and searched by its material/item code.
function openTakeoffLineInNewTab(tli: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment): void {
  const url = createProductOfferingScopeUrl(tli.readyPlan.id, tli.readyPlan.version.id, {
    search: tli.materialVariant?.code ?? tli.item.fullCode,
    filter: { costCode: [tli.item.costCode.id] },
  });
  openNewTab(url);
}
